import { Calendar, MapPin, Users, Globe, Newspaper, Layers, Award } from "lucide-react";

// Trust badges — auto-pulled from FIRM_A's existing dossier. Designed to lend instant
// credibility on a public pitch page without asking the operator to fill anything in.
// Renders as a horizontal strip of small chips. Hidden if fewer than 2 signals available.
export default function TrustBadges({ firmName, trust }) {
    if (!trust) return null;

    const yearsActive = trust.founded_year ? Math.max(1, new Date().getFullYear() - Number(trust.founded_year)) : null;

    const badges = [
        yearsActive && {
            key: "since",
            icon: Calendar,
            label: `Since ${trust.founded_year}`,
            sub: yearsActive === 1 ? "<1 yr" : `${yearsActive} yrs in market`,
        },
        trust.headquarters && {
            key: "hq",
            icon: MapPin,
            label: trust.headquarters.split(",")[0] || trust.headquarters,
            sub: trust.region ? `${trust.region} HQ` : "Headquartered",
        },
        trust.company_size && {
            key: "size",
            icon: Users,
            label: trust.company_size,
            sub: "Team size",
        },
        trust.industry && {
            key: "industry",
            icon: Layers,
            label: trust.industry.split("·")[0]?.trim() || trust.industry,
            sub: "Domain",
        },
        trust.website && {
            key: "site",
            icon: Globe,
            label: (trust.website || "").replace(/^https?:\/\//, "").replace(/\/$/, "").slice(0, 22),
            sub: "Verified site",
            href: trust.website.startsWith("http") ? trust.website : `https://${trust.website}`,
        },
        trust.recent_news_count >= 3 && {
            key: "intel",
            icon: Newspaper,
            label: `${trust.recent_news_count} recent moves`,
            sub: "Actively tracked",
        },
        trust.products_count >= 2 && {
            key: "products",
            icon: Award,
            label: `${trust.products_count} product lines`,
            sub: "Validated revenue",
        },
    ].filter(Boolean);

    if (badges.length < 2) return null;

    return (
        <section className="border border-zinc-800 bg-zinc-950 mb-6 print:hidden" data-testid="trust-badges">
            <div className="px-5 py-3 border-b border-zinc-800 flex items-center justify-between">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">
                    Why trust <span className="text-zinc-100">{firmName}</span>
                </div>
                <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-600">Auto-verified</div>
            </div>
            <div className="px-5 py-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
                {badges.map((b) => {
                    const Icon = b.icon;
                    const inner = (
                        <>
                            <div className="w-7 h-7 border border-zinc-800 bg-black flex items-center justify-center shrink-0">
                                <Icon className="w-3.5 h-3.5 text-amber-500" />
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="text-xs font-semibold text-zinc-100 truncate" data-testid={`trust-${b.key}-label`}>{b.label}</div>
                                <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500 truncate">{b.sub}</div>
                            </div>
                        </>
                    );
                    const className = "flex items-center gap-2.5 border border-zinc-800 bg-black/40 px-3 py-2 hover:border-amber-500/50 transition-colors";
                    return b.href ? (
                        <a key={b.key} href={b.href} target="_blank" rel="noopener noreferrer" className={className} data-testid={`trust-badge-${b.key}`}>{inner}</a>
                    ) : (
                        <div key={b.key} className={className} data-testid={`trust-badge-${b.key}`}>{inner}</div>
                    );
                })}
            </div>
        </section>
    );
}
